import AsyncStorage from '@react-native-async-storage/async-storage';
import {
  canLoginWithBiometric,
  getRefreshToken,
  getSharedPreferenceValue,
} from '@varo/expo-authentication';
import {
  authenticateAsync,
  hasHardwareAsync,
  isEnrolledAsync,
  LocalAuthenticationResult,
  supportedAuthenticationTypesAsync,
} from 'expo-local-authentication';
import { isAndroid, isWeb } from 'shared/constants/platform';
import {
  getAndroidUserIDForBiometricsEmailLoginKey,
  getAndroidUserIDForBiometricsPhoneLoginKey,
  getBiometricToggleSettingKey,
  getExistingUserIDForBiometricsKey,
  getExistingUserRegistrationForDevice,
  getHasPermanentRefreshTokenKey,
  getRefreshTokenKeyExistingiOSUser,
  getRefreshTokenKeyReactNativeUser,
  getUserAlreadyRegisteredBiometricsKey,
  getUserHasEnabledBiometricsKey,
  getUserIDForBiometricsKey,
} from 'shared/providers/environment';
import { addTag, logBreadcrumb, logError } from 'shared/utils/sentry/sentry';
import { SentryTags } from 'shared/utils/sentry/sentry.constants';

enum BiometricErrorEnum {
  notAvailable = 'not_available',
  userCancel = 'user_cancel',
  lockOut = 'lockout',
  userFallBack = 'user_fallback',
}

interface EncryptedStorageInterface {
  getItem: (key: string) => Promise<string | null>;
  setItem: (key: string, value: string) => Promise<void>;
  removeItem: (key: string) => Promise<void>;
}

let encryptedStorage: EncryptedStorageInterface | undefined;

export const getEncryptedStorage = async () => {
  if (!encryptedStorage && !isWeb) {
    try {
      const { default: EncryptedStorage } = await import(
        'react-native-encrypted-storage'
      );
      encryptedStorage = EncryptedStorage;
    } catch (error) {
      logBreadcrumb({
        level: 'error',
        message: `Error while getting EncryptedStorage: ${error}`,
      });
    }
  }
  return encryptedStorage;
};

export const getExistingRefreshTokenForiOSUser = async () => {
  try {
    const storage = await getEncryptedStorage();

    const [refreshToken, userID, alreadyRegisteredBiometrics] =
      await Promise.all([
        storage?.getItem(getRefreshTokenKeyExistingiOSUser()),
        storage?.getItem(getExistingUserIDForBiometricsKey()),
        storage?.getItem(getUserAlreadyRegisteredBiometricsKey()),
      ]);

    if (refreshToken === null || refreshToken === undefined) {
      return true;
    }

    await saveRecordInReactStorage(refreshToken);

    if (alreadyRegisteredBiometrics === 'false') {
      await saveBiometricToggleSetting(false);
    }

    if (userID) {
      await saveUserRegistrationForDevice(
        userID,
        alreadyRegisteredBiometrics ?? 'true',
      );
      await storage?.removeItem(getRefreshTokenKeyExistingiOSUser());
    }

    return refreshToken;
  } catch (error) {
    logBreadcrumb({
      level: 'error',
      message: `Error in getExistingRefreshTokenForiOSUser: ${error}`,
    });
    addTag(SentryTags.FRAUD, 'getExistingRefreshTokenForiOSUser');
    logError(error);
    return null;
  }
};
export const saveRecordInReactStorage = async (token: string) => {
  await saveRefreshTokenForReactNative(token);
  await saveHasUserRegistered(true);
  await saveBiometricToggleSetting(true);
};

export const saveUserRegistrationForDevice = async (
  userID: string,
  biometricsValue: string,
) => {
  const storage = await getEncryptedStorage();

  await saveUserIDForReactNative(userID);

  await storage?.setItem(
    getExistingUserRegistrationForDevice(),
    JSON.stringify(biometricsValue === 'true'),
  );
};

export const getExistingRefreshTokenForAndroidUser = async () => {
  const androidRefreshTokenAvailable = canLoginWithBiometric();
  if (androidRefreshTokenAvailable) {
    const token = getRefreshToken();

    const androidBiometricUserID =
      getSharedPreferenceValue(getAndroidUserIDForBiometricsEmailLoginKey()) ??
      getSharedPreferenceValue(getAndroidUserIDForBiometricsPhoneLoginKey());

    if (androidBiometricUserID) {
      await saveUserRegistrationForDevice(androidBiometricUserID, 'true');
    }
    await saveRecordInReactStorage(token);

    return token;
  }
};

export const getRefreshTokenForReactUser = async () => {
  try {
    const token = await AsyncStorage.getItem(
      getRefreshTokenKeyReactNativeUser(),
    );
    return token || null;
  } catch (error) {
    logBreadcrumb({
      level: 'info',
      message: `Error Retrieving refresh token: ${error}`,
    });
    return null;
  }
};

export const getBiometricUserId = async () => {
  try {
    const userID = await AsyncStorage.getItem(getUserIDForBiometricsKey());
    return userID ?? null;
  } catch (error) {
    logBreadcrumb({
      level: 'info',
      message: `Error Fetching biometric user ID: ${error}`,
    });
    return null;
  }
};

export const getBiometricToggleSetting = async () => {
  try {
    const toggleSetting = await AsyncStorage.getItem(
      getBiometricToggleSettingKey(),
    );
    return toggleSetting !== 'false';
  } catch (error) {
    logBreadcrumb({
      level: 'info',
      message: `Error Getting biometric toggle setting: ${error}`,
    });

    return false;
  }
};

export const saveRefreshTokenForReactNative = async (
  token: string,
): Promise<void> => {
  try {
    await AsyncStorage.setItem(getRefreshTokenKeyReactNativeUser(), token);
  } catch (error) {
    logBreadcrumb({
      level: 'info',
      message: `Error while save the RefreshToken: ${error}`,
    });
  }
};

export const saveUserIDForReactNative = async (userID: string) => {
  if (isWeb) {
    return;
  }

  try {
    await AsyncStorage.setItem(getUserIDForBiometricsKey(), userID);
  } catch (error) {
    logBreadcrumb({
      level: 'info',
      message: `Error while save the UserID: ${error}`,
    });
  }
};

export const saveHasUserRegistered = async (hasRegister: boolean) => {
  try {
    await AsyncStorage.setItem(
      getUserHasEnabledBiometricsKey(),
      JSON.stringify(hasRegister),
    );
  } catch (error) {
    logBreadcrumb({
      level: 'info',
      message: `Error while save data for user registered: ${error}`,
    });

    return;
  }
};

export const saveBiometricToggleSetting = async (isOn: boolean) => {
  try {
    await AsyncStorage.setItem(
      getBiometricToggleSettingKey(),
      JSON.stringify(isOn),
    );
  } catch (error) {
    logBreadcrumb({
      level: 'info',
      message: `Error while save the BiometricToggleSetting: ${error}`,
    });

    return;
  }
};

export const getBiometricType = async () => {
  try {
    return await supportedAuthenticationTypesAsync();
  } catch {
    logBreadcrumb({
      level: 'info',
      message: `Error while getting BiometricType`,
    });

    return null;
  }
};

export const isUserRegistered = async () => {
  try {
    const isRegister = await AsyncStorage.getItem(
      getUserHasEnabledBiometricsKey(),
    );
    return isRegister !== 'false' && isRegister !== null;
  } catch {
    logBreadcrumb({
      level: 'info',
      message: `Error while getting isUserRegistered`,
    });

    return false;
  }
};

export const saveHasPermanentRefreshToken = async (
  hasPermanentToken: boolean,
) => {
  try {
    await AsyncStorage.setItem(
      getHasPermanentRefreshTokenKey(),
      JSON.stringify(hasPermanentToken),
    );
  } catch (error) {
    logBreadcrumb({
      level: 'info',
      message: `Error while save PermanentToken: ${error}`,
    });

    return;
  }
};

export const hasPermanentRefreshToken = async () => {
  try {
    const isPermanentRefreshToken = await AsyncStorage.getItem(
      getHasPermanentRefreshTokenKey(),
    );
    return (
      isPermanentRefreshToken !== 'false' && isPermanentRefreshToken !== null
    );
  } catch {
    logBreadcrumb({
      level: 'info',
      message: `Error while getting isPermanentRefreshToken`,
    });

    return false;
  }
};

export const deleteUserRegisteredKey = async () => {
  await AsyncStorage.removeItem(getUserHasEnabledBiometricsKey());
};

export const deleteRefreshTokenForReactNativeUserKey = async () => {
  await AsyncStorage.removeItem(getRefreshTokenKeyReactNativeUser());
};

export async function shouldShowBiometricButton() {
  const isDisabledInApp = await getBiometricToggleSetting();
  if (!isDisabledInApp) {
    return false;
  }

  const hasHardware = await hasHardwareAsync();
  if (!hasHardware) {
    return false;
  }

  const isEnrolled = await isEnrolledAsync();
  if (!isEnrolled) {
    return false;
  }

  const refreshToken = await getRefreshTokenForReactUser();
  const hasValidRefreshToken = refreshToken !== null;
  if (!hasValidRefreshToken) {
    return false;
  }

  const hasBiometricRequirements =
    isDisabledInApp && hasHardware && hasValidRefreshToken && isEnrolled;

  return hasBiometricRequirements;
}

export async function isMigratedUser(): Promise<boolean> {
  try {
    const isRegistered = await isUserRegistered();
    if (isRegistered) {
      return true;
    }

    const refreshTokenCheck = isAndroid
      ? await getExistingRefreshTokenForAndroidUser()
      : await getExistingRefreshTokenForiOSUser();

    return refreshTokenCheck !== null;
  } catch (error) {
    logBreadcrumb({
      level: 'error',
      message: `Error in isMigratedUser: ${error}`,
    });
    addTag(SentryTags.FRAUD, 'isMigratedUser');
    logError(error);
    return false;
  }
}
export const askForBiometrics = async () =>
  await authenticateAsync({
    disableDeviceFallback: true,
    promptMessage: 'Enable biometrics login',
    cancelLabel: 'Not now',
    fallbackLabel: '',
  });

export const enableBiometrics = async (
  errorHandler?: (result: LocalAuthenticationResult) => void,
): Promise<boolean> => {
  try {
    const biometricsResult = await askForBiometrics();

    const isBiometricSuccess = biometricsResult?.success ?? false;

    if (!isBiometricSuccess) {
      if (
        biometricsResult.success === false &&
        biometricsResult.error === BiometricErrorEnum.lockOut.valueOf()
      ) {
        const isScreenLockSuccess = await handleScreenLock(
          biometricsResult?.warning,
        );
        if (isScreenLockSuccess) {
          await saveHasUserRegistered(isScreenLockSuccess);
          await saveBiometricToggleSetting(isScreenLockSuccess);
          return isScreenLockSuccess;
        }
      } else {
        errorHandler?.(biometricsResult);
      }
    }

    await saveHasUserRegistered(isBiometricSuccess);
    await saveBiometricToggleSetting(isBiometricSuccess);
    return isBiometricSuccess;
  } catch (error) {
    logBreadcrumb({
      level: 'info',
      message: `Error while Biometric enablement failed: ${error}`,
    });
    return false;
  }
};

export const handleScreenLock = async (warning?: string): Promise<boolean> => {
  const screenLockResult = await authenticateAsync({
    disableDeviceFallback: false,
    promptMessage: warning ?? 'Try to Login with username and password',
    cancelLabel: 'Cancel',
  });

  return screenLockResult.success ?? false;
};

export const authenticateWithBiometric = async () => {
  if (isWeb) {
    return null;
  }
  try {
    const biometricsResult = await authenticateAsync({
      disableDeviceFallback: true,
      promptMessage: 'Login with biometrics',
      cancelLabel: 'Cancel',
    });

    if (
      !biometricsResult?.success &&
      biometricsResult.error === BiometricErrorEnum.lockOut.valueOf()
    ) {
      const isScreenLockSuccess = await handleScreenLock(
        biometricsResult?.warning,
      );
      if (!isScreenLockSuccess) {
        logBreadcrumb({
          level: 'info',
          message: `Error while authenticateWithBiometric or screenLock`,
        });

        return null;
      }
    } else if (
      !biometricsResult?.success &&
      (biometricsResult.error === BiometricErrorEnum.notAvailable.valueOf() ||
        biometricsResult.error === BiometricErrorEnum.userCancel.valueOf() ||
        biometricsResult.error === BiometricErrorEnum.userFallBack.valueOf())
    ) {
      return null;
    }

    const token = await AsyncStorage.getItem(
      getRefreshTokenKeyReactNativeUser(),
    );
    return token;
  } catch (error) {
    logBreadcrumb({
      level: 'info',
      message: `Error while authenticateWithBiometric: ${error}`,
    });

    return null;
  }
};
